import apiClient from '../client';
import type {
  UserProfile,
  ProfileUpdate,
  DirectoryEntry,
  DirectMessage as DirectMessageType,
  MessageListItem,
  MessageCreate,
  UnreadCount,
} from '../../types/social';
import type {
  ApprovalRunResponse as CalcRunResponse,
  ApprovalSummary,
  ApprovalNotification as ApprovalNotif,
  NotificationCount,
  ConsultantRankInfo,
  ApprovalActionRequest,
} from '../../types/approval';

// ─── Profile Endpoints ───────────────────────────────────────────────────────

export const profiles = {
  getMyProfile: async (): Promise<UserProfile> => {
    const response = await apiClient.get<UserProfile>('/profiles/me');
    return response.data;
  },

  updateMyProfile: async (data: ProfileUpdate): Promise<UserProfile> => {
    const response = await apiClient.put<UserProfile>('/profiles/me', data);
    return response.data;
  },

  getUserProfile: async (userId: string): Promise<UserProfile> => {
    const response = await apiClient.get<UserProfile>(`/profiles/${userId}`);
    return response.data;
  },

  getDirectory: async (params?: {
    search?: string;
    skip?: number;
    limit?: number;
  }): Promise<DirectoryEntry[]> => {
    const response = await apiClient.get<DirectoryEntry[]>('/profiles/directory', {
      params,
    });
    return response.data;
  },
};

// ─── Messages Endpoints ─────────────────────────────────────────────────────

export const messages = {
  getInbox: async (params?: {
    skip?: number;
    limit?: number;
  }): Promise<MessageListItem[]> => {
    const response = await apiClient.get<MessageListItem[]>('/messages/inbox', {
      params,
    });
    return response.data;
  },

  getSent: async (params?: {
    skip?: number;
    limit?: number;
  }): Promise<MessageListItem[]> => {
    const response = await apiClient.get<MessageListItem[]>('/messages/sent', {
      params,
    });
    return response.data;
  },

  send: async (data: MessageCreate): Promise<DirectMessageType> => {
    const response = await apiClient.post<DirectMessageType>('/messages', data);
    return response.data;
  },

  getMessage: async (id: string): Promise<DirectMessageType> => {
    const response = await apiClient.get<DirectMessageType>(`/messages/${id}`);
    return response.data;
  },

  deleteMessage: async (id: string): Promise<void> => {
    await apiClient.delete(`/messages/${id}`);
  },

  getUnreadCount: async (): Promise<UnreadCount> => {
    const response = await apiClient.get<UnreadCount>('/messages/unread-count');
    return response.data;
  },
};

// ─── Approval Workflow Endpoints ─────────────────────────────────────────────

export const approvals = {
  listRuns: async (params?: {
    year?: number;
    status?: string;
  }): Promise<CalcRunResponse[]> => {
    const response = await apiClient.get<CalcRunResponse[]>('/approvals/runs', {
      params,
    });
    return response.data;
  },

  getRun: async (runId: string): Promise<CalcRunResponse> => {
    const response = await apiClient.get<CalcRunResponse>(
      `/approvals/runs/${runId}`
    );
    return response.data;
  },

  getSummary: async (): Promise<ApprovalSummary> => {
    const response = await apiClient.get<ApprovalSummary>('/approvals/summary');
    return response.data;
  },

  reviewRun: async (
    runId: string,
    data: ApprovalActionRequest
  ): Promise<CalcRunResponse> => {
    const response = await apiClient.post<CalcRunResponse>(
      `/approvals/runs/${runId}/review`,
      data
    );
    return response.data;
  },

  getNotifications: async (params?: {
    unread_only?: boolean;
    limit?: number;
  }): Promise<ApprovalNotif[]> => {
    const response = await apiClient.get<ApprovalNotif[]>(
      '/approvals/notifications',
      { params }
    );
    return response.data;
  },

  getNotificationCount: async (): Promise<NotificationCount> => {
    const response = await apiClient.get<NotificationCount>(
      '/approvals/notifications/count'
    );
    return response.data;
  },

  markNotificationsRead: async (
    notificationIds: string[]
  ): Promise<{ marked_read: number }> => {
    const response = await apiClient.post<{ marked_read: number }>(
      '/approvals/notifications/mark-read',
      { notification_ids: notificationIds }
    );
    return response.data;
  },

  getRanks: async (): Promise<ConsultantRankInfo[]> => {
    const response = await apiClient.get<ConsultantRankInfo[]>(
      '/approvals/ranks'
    );
    return response.data;
  },
};
